/**
 * Fetches selectable templates for a channel, normalized for the Bulk Send wizard.
 * - WhatsApp: 360dialog cloud templates, filtered to APPROVED only (Meta-approved).
 * - Email/SMS/Push: local templates from /api/template/{channel}.
 *
 * `variableCount` is the highest {{n}} placeholder index found in the body — i.e.
 * how many positional variables (CSV columns) the template expects.
 */

import { useState, useCallback } from 'react';
import { useHttpClient } from '@actschurch/ccms-sdk';
import { getApiUrl } from '../../../../config/ccms';

export interface ChannelTemplate {
  id: string;
  name: string;
  language?: string | null;
  body: string;
  status?: string | null;
  variableCount: number;
  /**
   * Push only: the MOBILE app the template targets (constellation appId, e.g. "ccms.task"). Lets the
   * bulk wizard pre-select the campaign's target app — that app selects the APNs topic.
   */
  mobileAppId?: string | null;
}

/** Guard against duplicate Select option values (Mantine throws on duplicates). */
function dedupeById(items: ChannelTemplate[]): ChannelTemplate[] {
  return Array.from(new Map(items.map((m) => [m.id, m])).values());
}

function maxPlaceholder(body: string): number {
  let max = 0;
  const re = /\{\{\s*(\d+)\s*\}\}/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(body)) !== null) max = Math.max(max, parseInt(m[1], 10));
  return max;
}

export function useChannelTemplates() {
  const httpClient = useHttpClient();
  const [loading, setLoading] = useState(false);
  const [templates, setTemplates] = useState<ChannelTemplate[]>([]);

  const fetchTemplates = useCallback(
    async (channel: 'email' | 'sms' | 'whatsapp' | 'push') => {
      setLoading(true);
      try {
        if (channel === 'whatsapp') {
          const resp = await httpClient.get<{ templates?: any[] }>(
            getApiUrl('communications', '/api/dialog360/templates?limit=1000&offset=0&sort=business_templates.name'),
          );
          const items = (resp.data as any)?.templates ?? [];
          const mapped: ChannelTemplate[] = items
            .filter((t: any) => String(t.status || '').toLowerCase() === 'approved')
            .map((t: any) => {
              const bodyComp = (t.components || []).find((c: any) => String(c.type || '').toUpperCase() === 'BODY');
              const body = bodyComp?.text || '';
              // Names can repeat across languages — use the unique 360dialog id as the key.
              const id = t.id || t.externalId || `${t.name}|${t.language || ''}`;
              return {
                id: String(id),
                name: String(t.name ?? '(untitled)'),
                language: t.language,
                body,
                status: t.status,
                variableCount: maxPlaceholder(body),
              };
            });
          setTemplates(dedupeById(mapped));
        } else {
          const resp = await httpClient.get<any[]>(getApiUrl('communications', `/api/template/${channel}`));
          const items = (resp.data as any[]) ?? [];
          // View models differ per channel: name is `templateName`; body is `body`
          // (email/push) or `message` (sms). Coalesce so labels/values are never undefined.
          const mapped: ChannelTemplate[] = items.map((t: any, i: number) => {
            const body = String(t.body ?? t.message ?? t.template ?? '');
            const id = String(t.templateID ?? t.templateId ?? t.id ?? `tpl-${i}`);
            const name = String(t.templateName ?? t.name ?? t.title ?? t.subject ?? `Template ${id}`);
            return { id, name, body, variableCount: maxPlaceholder(body), mobileAppId: t.mobileAppId ?? null };
          });
          setTemplates(dedupeById(mapped));
        }
      } catch {
        setTemplates([]);
      } finally {
        setLoading(false);
      }
    },
    [httpClient],
  );

  return { loading, templates, fetchTemplates };
}
